import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUtensils,
  FaBed,
  FaStethoscope,
  FaHandHoldingHeart,
  FaRegCheckCircle,
} from "react-icons/fa";
import { RotatingLines } from "react-loader-spinner";
import { useSearchWorkers } from "../../../context/SearchWorkersContext";
import { useJobCreate } from "../../../context/JobCreateContext";

const ICON_MAPPING = {
  "Daily living, social and community activities": <FaUtensils size={24} />,
  "Personal care": <FaBed size={24} />,
  Nursing: <FaStethoscope size={24} />,
  "Allied health": <FaHandHoldingHeart size={24} />,
};

const WorkerSupportTypesComponent = ({
  onUpdate,
  isSupportTypeDetailsError,
  clearError,
}) => {
  const { supportTypes, fetchSupportTypes } = useSearchWorkers();
  const {
    fetchJobSelectionsById,
    isJobSelectionsReady,
    getJobCreateIdFromCookies,
  } = useJobCreate();
  const [selectedSupportTypeDetails, setSelectedSupportTypeDetails] = useState(
    []
  );

  const [computedErrors, setComputedErrors] = useState([]);
  const [visibleErrors, setVisibleErrors] = useState([]);

  const supportTypeDetailsRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    const jobCreateId = getJobCreateIdFromCookies();
    if (!jobCreateId) {
      navigate("/account/fs-dashboard/my-jobs/new/standard");
    } else {
      (async () => {
        try {
          const data = await fetchJobSelectionsById(jobCreateId);

          setSelectedSupportTypeDetails(data?.supportTypeDetails || []);
        } catch (error) {
          console.error(
            "[WorkerSupportTypesComponent] Error fetching job selection:",
            error
          );
        }
      })();
    }
  }, [fetchJobSelectionsById, getJobCreateIdFromCookies, navigate]);

  useEffect(() => {
    fetchSupportTypes().catch((error) =>
      console.error("Error fetching support types:", error)
    );
  }, [fetchSupportTypes]);

  useEffect(() => {
    console.log(
      "[WorkerSupportTypesComponent] isJobSelectionsReady:",
      isJobSelectionsReady
    );
  }, [isJobSelectionsReady]);

  useEffect(() => {
    if (isSupportTypeDetailsError) {
      const toShow = computedErrors.length
        ? computedErrors
        : validateSupportTypes(selectedSupportTypeDetails);
      setVisibleErrors(toShow);
      smoothScrollToTop();
    } else {
      setVisibleErrors([]);
    }
  }, [isSupportTypeDetailsError]);


  useEffect(() => {
    if (selectedSupportTypeDetails.length > 0) {
      console.log("✅ Preloaded support types found. Running notifyUpdate...");
      notifyUpdate(selectedSupportTypeDetails);
    }
  }, [selectedSupportTypeDetails]);

  const smoothScrollToTop = () => {
    const container = supportTypeDetailsRef.current;
    if (container) {
      const scrollDuration = 600;
      const scrollStep = container.scrollTop / (scrollDuration / 15);

      const scrollInterval = setInterval(() => {
        if (container.scrollTop > 0) {
          container.scrollTop -= scrollStep;
        } else {
          clearInterval(scrollInterval);
        }
      }, 15);
    }
  };

  const validateSupportTypes = (supportTypeSelections) => {
    const errors = [];
    if (!supportTypeSelections || supportTypeSelections.length === 0) {
      errors.push({
        field: "supportTypes",
        message: "Please make at least one selection.",
      });
    }
    console.log("🚨 validateSupportTypes Errors:", errors);
    return errors;
  };


  const notifyUpdate = (newSelections) => {
    const errors = validateSupportTypes(newSelections);
    setComputedErrors(errors);
    onUpdate({ data: newSelections, errors, isValid: errors.length === 0 });

    if (isSupportTypeDetailsError) setVisibleErrors(errors);
    else setVisibleErrors([]);
  };


  const toggleSupportLabel = (label) => {
    setSelectedSupportTypeDetails((prev) => {
      let updated;
      if (prev.includes(label)) {
        updated = prev.filter((type) => type !== label);
        console.log(`[toggleSupportLabel] Removing label: "${label}"`);
      } else {
        updated = [...prev, label];
        console.log(`[toggleSupportLabel] Adding label: "${label}"`);
      }
      console.log("[toggleSupportLabel] Updated Support Labels:", updated);
      setTimeout(() => {
        notifyUpdate(updated);
      }, 0);
      return updated;
    });
  };

  const getIconColor = (index) => {
    const colors = ["#58735b", "#907d34", "#009688", "#00bcd4"];
    return colors[index % colors.length];
  };

  const SupportType = ({ label, description, index }) => (
    <div
      onClick={() => toggleSupportLabel(label)}
      className={`support-type ${selectedSupportTypeDetails.includes(label)
          ? "support-type-selected"
          : ""
        }`}
    >
      {selectedSupportTypeDetails.includes(label) ? (
        <div className="wstc-icon-selected">
          <FaRegCheckCircle size={34} />
        </div>
      ) : (
        <div
          className="wstc-icon"
          style={{ backgroundColor: getIconColor(index) }}
        >
          {ICON_MAPPING[label]}
        </div>
      )}
      <div className="support-type-description">
        <h3>{label}</h3>
        <p>{description}</p>
      </div>
    </div>
  );

  return (
    <div className="worker-support-types-container" ref={supportTypeDetailsRef}>
      {/* <h2>Select worker support areas</h2> */}
      {isSupportTypeDetailsError && visibleErrors.length > 0 && (
        <div className="wstc-error-message" >
          <ul>
            {visibleErrors.map((error, index) => (
              <li key={index}>{error.message}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="support-types-scrollable" >
        {supportTypes.length > 0 ? (
          supportTypes.map((supportType, index) => (
            <SupportType
              key={index}
              label={supportType.label}
              description={supportType.description}
              index={index}
            />
          ))
        ) : (
          <div style={{ textAlign: "left", marginLeft: "20px" }}>
            <RotatingLines
              visible={true}
              height="76"
              width="36"
              color="grey"
              strokeWidth="3"
              animationDuration="0.75"
              ariaLabel="rotating-lines-loading"
              wrapperStyle={{}}
              wrapperClass=""
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkerSupportTypesComponent;
